import app from 'app';
import _ from 'lodash-extended';
import { AdjustOverlayAssetPlugin } from './plugins/AdjustOverlayAsset';
import fg from '#/libs/FrameGrid';

/**
 * round floating point to a local context precision
 * @param float
 * @return {number}
 */
function round(float) {
  return app.sfp(float, app.FP_ROUND);
}

function isNumber(that) {
  return _.isNumber(that) && !isNaN(that);
}

function closeEnough(time1, time2) {
  return (
    Math.abs(round(time2 - time1)) <= app.TIME.MAX_OVERLAY_GAP_FOR_ANIMATION_CUT
  );
}

class GapDescriptor {
  /**
   * @param start {number}
   * @param end {number}
   */
  constructor({ start, end }) {
    this.start = start;
    this.end = end;
  }
}

/**
 * Controller for managing insertion, modification and removal of
 * scene foreground assets
 *
 * Assumes that time properties names are similar to ones in timeline tracks
 *
 * @example
 * const pipa = new PictureInPictureAdapter({scene});
 * if (pipa.canFit({overlay: newAsset, time: 30})) {
 *   pipa.insert({asset: newAsset, time: 30})
 * }
 */
class PictureInPictureAdapter {
  /**
   * @param scene {object} asset
   * @param options
   */
  constructor({ scene }, options) {
    this.scene = scene;
    if (!(this.scene[PictureInPictureAdapter.TRACK_PROP] instanceof Array)) {
      this.scene[PictureInPictureAdapter.TRACK_PROP] = [];
    }

    this.plugins = new app.namespace.Util.PluginManager();
    this.plugins.add(new AdjustOverlayAssetPlugin({ pipa: this }));
    this.selectedOrdGuid = null;
    this.options = _.options(options, {
      /** log */
      verbose: true,
    });

    for (const overlay of this.track) {
      if (!overlay.guid) {
        overlay.guid = app.guid();
      }
    }
  }

  static get TRACK_PROP() {
    return 'overlayTracks';
  }

  static hasThisOverlay(scene, overlay) {
    return _.some(scene[PictureInPictureAdapter.TRACK_PROP], {
      id: _.isString(overlay) ? overlay : overlay.id,
    });
  }

  static hasThisCustomOverlay(scene, overlay) {
    let ids = [];

    if (scene.agd.customOverlays) {
      const fields = _.flatten(scene.agd.customOverlays.map((co) => co.fields));
      const mediaFieldValues = fields
        .filter((f) => f.type === 'MEDIA')
        .map((f) => f.value);
      if (mediaFieldValues) {
        ids = mediaFieldValues.map((value) => {
          const o = JSON.parse(value);
          return o.id;
        });
      }
    }

    return ids.includes(_.isString(overlay) ? overlay : overlay.id);
  }

  static track(scene) {
    const trackProp = PictureInPictureAdapter.TRACK_PROP;
    // create track if absent
    if (!(scene[trackProp] instanceof Array)) {
      scene[trackProp] = [];
    }
    return scene[trackProp];
  }

  getThemeInfo() {
    return app.rootStore.state?.story.themeInfo;
  }

  /**
   * Get current scene track
   * @return {Array}
   */
  get track() {
    return PictureInPictureAdapter.track(this.scene);
  }

  set track(array) {
    this.scene[PictureInPictureAdapter.TRACK_PROP] = array;
  }

  log(...args) {
    this.options.verbose &&
      console.log(
        '%cPIPA:',
        `
      font-weight: 700;
      color: #000;
      background-color: white;
      padding: 0px 4px;
      border-left: 10px solid #bbb;
      border-right: 10px solid #bbb;
      text-shadow: 1px 1px 2px rgba(0,0,0, 0.8);
    `,
        ...args
      );
    return this;
  }

  error(msg, bug) {
    app.doctorController.report(`PIPA: ${msg}`, bug);
    return this;
  }

  /**
   * sort tracks by start time ascending
   * @return {PictureInPictureAdapter}
   */
  sort() {
    this.track.sort((a, b) =>
      a.displayStartTime < b.displayStartTime
        ? -1
        : a.displayStartTime === b.displayStartTime
        ? 0
        : 1
    );
    return this;
  }

  toLocal(absoluteTime) {
    return fg.round(absoluteTime - this.scene.displayStartTime);
  }

  toAbsolute(localTime) {
    return fg.round(this.scene.displayStartTime + localTime);
  }

  /**
   * Check if requested time not inside asset
   *
   * @param localTime {number}
   * @return {boolean}
   */
  isAvailable(localTime) {
    const isInsideSome = _.some(this.track, (item) => {
      return (
        item.displayStartTime <= localTime && localTime < item.displayEndTime
      );
    });
    const isOutsideScene =
      localTime < 0 || this.scene.displayDuration <= localTime;
    return !isInsideSome && !isOutsideScene;
  }

  /**
   * Given an Overlay asset end time, check whether we can duplicate it.
   *
   * @param overlay {object}
   * @return {boolean}
   */
  canDuplicate(overlay) {
    const endTime = overlay.displayEndTime;
    if (!isNumber(endTime)) {
      return false;
    }

    return this.canFitBase({ overlay, localTime: endTime });
  }

  /**
   * Check if some overlay can be replaced
   * @param overlay
   * @param replacement
   * @return {boolean}
   */
  canReplaceOverlay(overlay, replacement) {
    let left = round(this.scene.displayDuration - overlay.displayStartTime);
    const curIndex = this.track.indexOf(overlay);
    const nextOverlay = this.track[curIndex + 1];
    if (nextOverlay) {
      left = nextOverlay.displayStartTime;
    }

    return replacement.limit.min <= left;
  }

  /**
   * assume canReplaceOverlay returned true
   * @param oldOverlay
   * @param newOverlay
   */
  replaceOverlay({ oldOverlay, newOverlay }) {
    if (!_.isObject(oldOverlay) || !_.isObject(newOverlay)) {
      this.error(
        `replaceOverlay: check arguments (${oldOverlay}, ${newOverlay})`
      );
      return;
    }

    const overlay = $.extend(true, {}, newOverlay);

    if (!overlay.guid) {
      overlay.guid = app.guid();
    }

    // inherit time slot
    overlay.displayStartTime = oldOverlay.displayStartTime;
    overlay.displayDuration = oldOverlay.displayDuration;
    overlay.displayEndTime = oldOverlay.displayEndTime;

    if (app.assetType.isFRAGMENT(overlay)) {
      overlay.endTime = round(overlay.startTime + overlay.displayDuration);
    }

    // replace
    const index = this.track.indexOf(oldOverlay);
    this.track[index] = overlay;

    this.plugins.trigger('afterReplaceOverlay', {
      overlay: overlay,
      oldOverlay: oldOverlay,
    });
  }

  changeOverlayTiming({ oldOverlay, newOverlay }) {
    if (!_.isObject(oldOverlay) || !_.isObject(newOverlay)) {
      this.error(
        `replaceOverlay: check arguments (${oldOverlay}, ${newOverlay})`
      );
      return;
    }

    const index = this.track.indexOf(oldOverlay);
    const overlay = this.track[index];
    // const prevOverlay = this.track[index - 1];
    // const nextOverlay = this.track[index + 1];
    const timingWindow = this.getMaxOverlayTimingWindow(overlay);
    console.log(timingWindow);

    const sceneDisplayDuration = this.scene.displayDuration;
    let duration = round(newOverlay.endTime - newOverlay.startTime);
    let displayStartTime = overlay.displayStartTime;
    // const startTime = newOverlay.startTime;
    // let endTime = newOverlay.endTime;

    if (duration > timingWindow.time) {
      duration = timingWindow.time;
      // endTime = round(startTime + duration);
    }

    if (displayStartTime + duration > sceneDisplayDuration) {
      displayStartTime -= displayStartTime + duration - sceneDisplayDuration;
    }

    const changes = {
      startTime: newOverlay.startTime,
      endTime: newOverlay.endTime,
      displayStartTime,
      displayEndTime: displayStartTime + duration,
      displayDuration: duration,
    };
    Object.assign(overlay, changes);
  }

  /**
   * How many time available from given
   * @note: assumes that given time isAvailable
   *
   * @param localTime {number}
   * @return {number}
   */
  getAvailableFrom(localTime) {
    const timeTillEnd = round(this.scene.displayDuration - localTime);

    // find first asset start time at right
    let rv = timeTillEnd;
    for (let n = 0, N = this.track.length; n < N; n++) {
      const item = this.track[n];
      if (localTime < item.displayStartTime) {
        rv = round(item.displayStartTime - localTime);
        break;
      }
    }

    return round(Math.min(rv, timeTillEnd));
  }

  canFitBase({ overlay, localTime }) {
    if (!('limit' in overlay && 'min' in overlay.limit)) {
      this.error('canFitBase: overlay asset limit expected');
      return false;
    }
    return (
      this.isAvailable(localTime) &&
      this.getAvailableFrom(localTime) >= overlay.limit.min
    );
  }

  getMaxOverlayTimingWindow(overlay) {
    const i = _.findIndex(this.track, { guid: overlay.guid });
    let minLeft = 0;
    let maxRight = this.scene.displayEndTime - this.scene.displayStartTime;
    const prevOverlay = this.track[i - 1];
    if (prevOverlay) {
      minLeft = prevOverlay.displayEndTime;
    }
    const nextOverlay = this.track[i + 1];
    if (nextOverlay) {
      maxRight = nextOverlay.displayStartTime;
    }
    return { time: round(maxRight - minLeft), left: minLeft, right: maxRight };
  }

  /**
   * Check if asset can fit into track at given time
   *
   * @param {{overlay, absoluteTime}}
   * @returns {boolean}
   */
  canFit({ overlay, absoluteTime }) {
    if (!_.isObject(overlay) || !isNumber(absoluteTime)) {
      this.error(`canFit: check arguments (${overlay}, ${absoluteTime})`);
      return false;
    }

    const localTime = this.toLocal(absoluteTime);
    return this.canFitBase({ overlay, localTime });
  }

  /**
   * Insert FA to scene at specific time
   * @note: assumes asset canFit
   *
   * Insertion happens if time slot is free from any asset
   * and free time is bigger than its limit.min
   * Inserted asset may loose some of its initial duration
   *
   * @return overlay
   */
  insert({ overlay, absoluteTime, desiredDuration, isNew = true }) {
    if (!_.isObject(overlay) || !isNumber(absoluteTime)) {
      this.error(`insert: check arguments (${overlay}, ${absoluteTime})`);
      return null;
    }

    if (isNew) {
      overlay = _.cloneDeepAndClean(overlay);
    }

    if (!overlay.guid) {
      overlay.guid = app.guid();
    }

    const localTime = this.toLocal(absoluteTime);
    let duration = desiredDuration || overlay.limit.initial;
    const available = this.getAvailableFrom(localTime);

    // limit to available
    if (duration > available) {
      duration = available;
      if (app.assetType.isFRAGMENT(overlay)) {
        overlay.endTime = fg.trunc(overlay.startTime + duration);
      }
    }

    overlay.displayStartTime = fg.trunc(localTime);
    overlay.displayDuration = duration;
    overlay.displayEndTime = fg.trunc(localTime + duration);

    this.track.push(overlay);
    this.sort();

    if (isNew) {
      this.plugins.trigger('afterInsert', { overlay });
    }
    return overlay;
  }

  /**
   * @param scene {object}
   * @param assignNewGuid {boolean}
   * @returns {object}
   */
  static duplicateScene({ scene, assignNewGuid }) {
    const copy = _.cloneDeepAndClean(scene);

    if (assignNewGuid) {
      copy.timelineItemId = copy.guid = app.guid();

      for (const overlay of copy[PictureInPictureAdapter.TRACK_PROP]) {
        overlay.guid = app.guid();
      }

      _.each(copy.agd.customOverlays, (customOverlay) => {
        customOverlay.timelineItemId = app.guid();
      });
    }

    return copy;
  }

  duplicateOverlay(overlayGuid) {
    for (let n = 0, N = this.track.length; n < N; n++) {
      const item = this.track[n];
      if (item.guid === overlayGuid) {
        const overlay = $.extend(true, {}, item);
        overlay.guid = app.guid();
        const localTime = overlay.displayEndTime;
        const available = this.getAvailableFrom(localTime);
        let duration = overlay.displayDuration;
        if (duration > available) {
          duration = available;
          if (app.assetType.isFRAGMENT(overlay)) {
            overlay.endTime = round(overlay.startTime + duration);
          }
        }
        overlay.displayStartTime = round(localTime);
        overlay.displayDuration = duration;
        overlay.displayEndTime = round(localTime + duration);

        this.track.push(overlay);
        this.sort();

        return overlay;
      }
    }
  }

  select(ordGuid) {
    this.selectedOrdGuid = ordGuid;
  }

  resetSelection() {
    this.selectedOrdGuid = null;
  }

  /**
   * Find FA by guid | time
   * @param by {Object} one of search params
   * @return {null|Object}
   */
  find({ guid, localTime, absoluteTime }) {
    let rv = null;

    if (guid) {
      for (let n = 0, N = this.track.length; n < N; n++) {
        const item = this.track[n];
        if (item.guid === guid) {
          rv = item;
          break;
        }
      }
    } else {
      if (isNumber(absoluteTime)) {
        localTime = this.toLocal(absoluteTime);
      }
      if (isNumber(localTime)) {
        for (let n = 0, N = this.track.length; n < N; n++) {
          const item = this.track[n];
          if (
            item.displayStartTime <= localTime &&
            localTime < item.displayEndTime
          ) {
            rv = item;
            break;
          }
        }
      }
    }

    return rv;
  }

  /**
   * Delete FA by guid
   * @param overlayGuid
   */
  delete(overlayGuid) {
    for (let n = 0, N = this.track.length; n < N; n++) {
      const item = this.track[n];
      if (item.guid === overlayGuid) {
        this.track.splice(n, 1);
        break;
      }
    }

    // RESERVED: this.plugins.trigger('afterDelete');
  }

  isEmpty() {
    return _.isEmpty(this.track);
  }

  static isEmpty(sceneAssetObj) {
    return _.isEmpty(sceneAssetObj[PictureInPictureAdapter.TRACK_PROP]);
  }

  getNumberOfExpiredOverlays() {
    return PictureInPictureAdapter.getNumberOfExpiredOverlays(this.scene);
  }

  static getNumberOfExpiredOverlays(scene) {
    let count = 0;

    _.eachIn(scene, 'agd.customOverlays.fields', (field) => {
      if (field.type === 'MEDIA') {
        const { expirationDate } = JSON.parse(field.value);
        if (expirationDate) {
          const dummy = { metadata: { expirationDate } };
          count += app.assetType.isExpired(dummy) ? 1 : 0;
        }
      }
    });

    count += _.sumBy(scene[PictureInPictureAdapter.TRACK_PROP], (overlay) => {
      return app.assetType.isExpired(overlay) ? 1 : 0;
    });

    return count;
  }

  /**
   * Apply scene changes
   * assuming this.scene has updated state,
   * @param originalScene
   */
  apply({ originalScene }) {
    this.ensureAnimationCut();
    originalScene[PictureInPictureAdapter.TRACK_PROP] = this.track;
  }

  /**
   * get copy of scene overlays during scene resize
   * @param [ordTimeWindow] {Object}
   * @return {Array<Object>}
   */
  getCopyOfTracksDuringResize(ordTimeWindow) {
    const rv = [];
    let track = this.track;

    // in case of defined time window - work on clone track
    if (ordTimeWindow) {
      track = _.cloneDeepAndClean(this.track);
    }

    ordTimeWindow = Object.assign(
      {
        START_TIME_SHIFT: 0,
        END_TIME_SHIFT: 0,
      },
      ordTimeWindow
    );

    if (ordTimeWindow.START_TIME_SHIFT > 0) {
      track = PictureInPictureAdapter.cutFromLeftEx({
        track,
        cutTime: ordTimeWindow.START_TIME_SHIFT,
      });
    } else if (ordTimeWindow.START_TIME_SHIFT < 0) {
      PictureInPictureAdapter.shiftOverlaysByEx({
        track,
        delta: Math.abs(ordTimeWindow.START_TIME_SHIFT),
      });
    } else if (ordTimeWindow.END_TIME_SHIFT !== 0) {
      track = PictureInPictureAdapter.cutFromRightEx({
        track,
        cutTime: this.toLocal(this.scene.displayEndTime),
      });
    }

    for (const overlay of track) {
      rv.push(overlay);
    }

    return rv;
  }

  ensureAnimationCut() {
    PictureInPictureAdapter.ensureAnimationCutEx({ scene: this.scene });
  }

  static checkAnimationAvailability({ scene, overlay }) {
    const state = {
      canAnimateAtLeft: true,
      canAnimateAtRight: true,
    };
    const track = scene[PictureInPictureAdapter.TRACK_PROP];
    const curIndex = _.findIndex(track, { guid: overlay.guid });
    const prev = track[curIndex - 1];
    const next = track[curIndex + 1];

    if (prev) {
      state.canAnimateAtLeft = !closeEnough(
        prev.displayEndTime,
        overlay.displayStartTime
      );
    } else {
      state.canAnimateAtLeft = !closeEnough(0, overlay.displayStartTime);
    }

    if (next) {
      state.canAnimateAtRight = !closeEnough(
        overlay.displayEndTime,
        next.displayStartTime
      );
    } else {
      state.canAnimateAtRight = !closeEnough(
        overlay.displayEndTime,
        scene.displayDuration
      );
    }

    return state;
  }

  static ensureAnimationCutEx({ scene }) {
    if (PictureInPictureAdapter.isEmpty(scene)) {
      return;
    }

    const track = scene[PictureInPictureAdapter.TRACK_PROP];
    for (let n = 0, N = track.length; n < N; n++) {
      const cur = track[n];
      const next = track[n + 1];

      if (!n && closeEnough(0, cur.displayStartTime)) {
        // first - cut on left
        cur.sceneLayout.inAnimationId = 'CUT';
      }

      if (next) {
        if (closeEnough(cur.displayEndTime, next.displayStartTime)) {
          // adjacent - cut on right/left
          cur.sceneLayout.outAnimationId = 'CUT';
          next.sceneLayout.inAnimationId = 'CUT';
        }
      } else {
        if (closeEnough(cur.displayEndTime, scene.displayDuration)) {
          // last - cut on right
          cur.sceneLayout.outAnimationId = 'CUT';
        }
      }
    }
  }

  /**
   * Get array of gaps in the scene time space
   * @note: gap descriptor times are local
   * @returns {Array}
   */
  getGapDescriptor() {
    const rv = [];
    const localEnd = this.toLocal(this.scene.displayEndTime);

    if (this.track.length) {
      let time = 0;
      let prevTrack = null;
      let track = this.track[0];

      // gap before first track
      time = round(track.displayStartTime - 0);
      if (time > app.TIME.MAX_INTERASSET_CLEARANCE) {
        rv.push(
          new GapDescriptor({
            start: 0,
            end: time,
          })
        );
      }

      // gaps between tracks
      for (let n = 1, N = this.track.length; n < N; n++) {
        prevTrack = track;
        track = this.track[n];
        time = round(track.displayStartTime - prevTrack.displayEndTime);
        if (time > app.TIME.MAX_INTERASSET_CLEARANCE) {
          rv.push(
            new GapDescriptor({
              start: prevTrack.displayEndTime,
              end: track.displayStartTime,
            })
          );
        }
      }

      // gap after last track
      time = round(localEnd - track.displayEndTime);
      if (time > app.TIME.MAX_INTERASSET_CLEARANCE) {
        rv.push(
          new GapDescriptor({
            start: track.displayEndTime,
            end: localEnd,
          })
        );
      }
    } else {
      rv.push(
        new GapDescriptor({
          start: this.toLocal(this.scene.displayStartTime),
          end: localEnd,
        })
      );
    }

    return rv;
  }

  /**
   * Get time window for overlay resize
   * @param overlay
   * @param [options]
   */
  getOverlayResizeTimeWindow(overlay, options) {
    options = _.options(options, { relativeToScene: false });
    const i = _.findIndex(this.track, { guid: overlay.guid });

    let maxDurationAtLeft = overlay.limit.max;
    let maxDurationAtRight = overlay.limit.max;
    if (overlay.limit.fragment) {
      maxDurationAtLeft = Math.min(
        maxDurationAtLeft,
        round(overlay.endTime - overlay.limit.fragment.min)
      );
      maxDurationAtRight = Math.min(
        maxDurationAtRight,
        round(overlay.limit.fragment.max - overlay.startTime)
      );
    }

    let minLeft = 0;
    const prevOverlay = this.track[i - 1];
    if (prevOverlay) {
      minLeft = prevOverlay.displayEndTime;
    }
    minLeft = Math.max(
      minLeft,
      round(overlay.displayEndTime - maxDurationAtLeft)
    );
    const maxLeft = round(overlay.displayEndTime - overlay.limit.min);

    const minRight = round(overlay.displayStartTime + overlay.limit.min);
    let maxRight = this.scene.displayDuration;
    const nextOverlay = this.track[i + 1];
    if (nextOverlay) {
      maxRight = nextOverlay.displayStartTime;
    }
    maxRight = Math.min(
      round(overlay.displayStartTime + maxDurationAtRight),
      maxRight
    );

    return {
      left: {
        min: options.relativeToScene ? minLeft : this.toAbsolute(minLeft),
        max: options.relativeToScene ? maxLeft : this.toAbsolute(maxLeft),
      },
      right: {
        min: options.relativeToScene ? minRight : this.toAbsolute(minRight),
        max: options.relativeToScene ? maxRight : this.toAbsolute(maxRight),
      },
    };
  }

  getSceneResizeTimeWindow(options) {
    options = _.options(options, { relativeToScene: false });

    const videoSpeed = this.scene.agd.videoSpeed || 1;
    const minDuration = this.scene.limit.min;
    let maxDurationAtLeft = this.scene.limit.max;
    let maxDurationAtRight = this.scene.limit.max;
    if (this.scene.limit.fragment) {
      maxDurationAtLeft = round(
        (this.scene.endTime - this.scene.limit.fragment.min) / videoSpeed
      );
      maxDurationAtRight = round(
        (this.scene.limit.fragment.max - this.scene.startTime) / videoSpeed
      );
    }

    const minLeft = round(this.scene.displayDuration - maxDurationAtLeft);
    const maxLeft = round(this.scene.displayDuration - minDuration);
    const minRight = minDuration;
    const maxRight = maxDurationAtRight;

    return {
      left: {
        // converted to min/max scene's duration
        minDuration: minDuration,
        maxDuration: maxDurationAtLeft,
        // timeline's time pointer
        min: options.relativeToScene ? minLeft : this.toAbsolute(minLeft),
        max: options.relativeToScene ? maxLeft : this.toAbsolute(maxLeft),
      },
      right: {
        // timeline's time pointer
        min: options.relativeToScene ? minRight : this.toAbsolute(minRight),
        max: options.relativeToScene ? maxRight : this.toAbsolute(maxRight),
      },
    };
  }

  shiftOverlaysBy(delta) {
    PictureInPictureAdapter.shiftOverlaysByEx({ track: this.track, delta });
  }

  static shiftOverlaysByEx({ track, delta }) {
    for (const overlay of track) {
      overlay.displayStartTime = round(overlay.displayStartTime + delta);
      overlay.displayEndTime = round(overlay.displayEndTime + delta);
    }
  }

  /**
   * @param absoluteTime {Number}
   * @return {Number}
   */
  findFirstFreeAtRight(absoluteTime) {
    const localTime = this.toLocal(absoluteTime);
    if (this.isAvailable(localTime)) {
      return absoluteTime;
    } else {
      // build array of gaps, then find gap start-time that is bigger than localTime
      const gaps = this.getGapDescriptor();
      for (let n = 0, N = gaps.length; n < N; n++) {
        const gap = gaps[n];
        if (localTime <= gap.start) {
          return this.toAbsolute(gap.start);
        }
      }
      return null;
    }
  }

  static sceneReplace({ oldScene, newScene }) {
    let arr = oldScene[PictureInPictureAdapter.TRACK_PROP] || [];

    if (newScene.displayDuration < oldScene.displayDuration) {
      arr = PictureInPictureAdapter.cutFromRightEx({
        track: arr,
        cutTime: newScene.displayDuration,
      });
    }

    newScene[PictureInPictureAdapter.TRACK_PROP] = arr;
  }

  /**
   * @param cutTime local time
   */
  cutFromRight(cutTime) {
    this.track = PictureInPictureAdapter.cutFromRightEx({
      track: this.track,
      cutTime,
    });
    PictureInPictureAdapter.ensureAnimationCutEx({ scene: this.scene });
  }

  /**
   * @param track
   * @param cutTime
   * @return {Array} new tracks
   */
  static cutFromRightEx({ track, cutTime }) {
    const arr = [];

    for (const overlay of track) {
      if (overlay.displayEndTime <= cutTime) {
        arr.push(overlay);
      } else if (overlay.displayStartTime < cutTime) {
        const newDuration = round(cutTime - overlay.displayStartTime);
        if (newDuration >= overlay.limit.min) {
          overlay.displayDuration = newDuration;
          overlay.displayEndTime = round(
            overlay.displayStartTime + newDuration
          );

          if (app.assetType.isFRAGMENT(overlay)) {
            overlay.endTime = round(overlay.startTime + newDuration);
          }

          arr.push(overlay);
        }
      }
    }

    return arr;
  }

  /**
   * @param cutTime local time
   */
  cutFromLeft(cutTime) {
    this.track = PictureInPictureAdapter.cutFromLeftEx({
      track: this.track,
      cutTime,
    });
    PictureInPictureAdapter.ensureAnimationCutEx({ scene: this.scene });
  }

  /**
   * @param track
   * @param cutTime
   * @return {Array} new tracks
   */
  static cutFromLeftEx({ track, cutTime }) {
    const arr = [];

    for (const overlay of track) {
      if (overlay.displayStartTime >= cutTime) {
        overlay.displayStartTime = round(overlay.displayStartTime - cutTime);
        overlay.displayEndTime = round(overlay.displayEndTime - cutTime);
        arr.push(overlay);
      } else if (overlay.displayEndTime > cutTime) {
        const newDuration = round(overlay.displayEndTime - cutTime);
        if (newDuration >= overlay.limit.min) {
          overlay.displayStartTime = 0;
          overlay.displayDuration = newDuration;
          overlay.displayEndTime = newDuration;

          if (app.assetType.isFRAGMENT(overlay)) {
            overlay.startTime = round(overlay.endTime - newDuration);
            // preview becomes irrelevant when changing start/end times
            delete overlay.previewResultData;
          }

          arr.push(overlay);
        }
      }
    }

    return arr;
  }

  static sceneSplit({ originalAsset, cloneAsset }) {
    const leftOverlays =
      originalAsset[PictureInPictureAdapter.TRACK_PROP] || [];
    const rightOverlays = cloneAsset[PictureInPictureAdapter.TRACK_PROP] || [];
    const time = {
      leftDuration: originalAsset.displayDuration,
      rightDuration: cloneAsset.displayDuration,
      cut: originalAsset.displayDuration,
    };

    for (let n = 0, N = leftOverlays.length; n < N; n++) {
      const left = leftOverlays[n];
      const right = rightOverlays[n];
      console.assert(left !== right, 'should not be the same object');
      const cut = {
        before: left.displayEndTime <= time.cut,
        after: left.displayStartTime >= time.cut,
      };

      if (cut.before) {
        right._REMOVED_ = true;
      } else if (cut.after) {
        left._REMOVED_ = true;

        right.displayStartTime = round(
          right.displayStartTime - time.leftDuration
        );
        right.displayEndTime = round(right.displayEndTime - time.leftDuration);
      } else {
        left.displayEndTime = time.leftDuration;
        left.displayDuration = round(
          left.displayEndTime - left.displayStartTime
        );
        if (left.displayDuration < left.limit.min) {
          left._REMOVED_ = true;
        } else {
          if (app.assetType.isFRAGMENT(left)) {
            left.endTime = round(left.startTime + left.displayDuration);
            delete left.previewResultData;
          }
        }

        right.displayStartTime = 0;
        right.displayEndTime = round(right.displayEndTime - time.leftDuration);
        right.displayDuration = round(
          right.displayEndTime - right.displayStartTime
        );
        if (right.displayDuration < right.limit.min) {
          right._REMOVED_ = true;
        } else {
          if (app.assetType.isFRAGMENT(right)) {
            right.startTime = round(right.endTime - right.displayDuration);
            delete right.previewResultData;
          }
        }
      }
    }

    // filter-out all marked for removal
    for (const asset of [originalAsset, cloneAsset]) {
      asset[PictureInPictureAdapter.TRACK_PROP] = _.filter(
        asset[PictureInPictureAdapter.TRACK_PROP],
        (overlay) => !overlay._REMOVED_
      );
    }

    PictureInPictureAdapter.ensureAnimationCutEx({ scene: originalAsset });
    PictureInPictureAdapter.ensureAnimationCutEx({ scene: cloneAsset });

    // reset clone's type of transition
    cloneAsset.agd.transition = {
      wiperType: null,
      wiperName: null,
      presetName: null,
      selectedByUser: false,
    };
  }
}

export default PictureInPictureAdapter;
